import { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { movies, categories, genres, regions, years } from '@/mocks/movies';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';

  const [activeGenre, setActiveGenre] = useState('全部');
  const [activeRegion, setActiveRegion] = useState('全部');
  const [activeYear, setActiveYear] = useState('全部');
  const [sortBy, setSortBy] = useState('latest');

  const category = categories.find((c) => c.slug === slug);
  const categoryName = category ? category.name : '全部';

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [slug, activeGenre, activeRegion, activeYear, sortBy]);

  useEffect(() => {
    setActiveGenre('全部');
    setActiveRegion('全部');
    setActiveYear('全部');
    setSortBy('latest');
  }, [slug]);

  const filteredMovies = useMemo(() => {
    let result = [...movies];

    if (slug && slug !== 'all') {
      result = result.filter((m) => m.category === slug);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.originalTitle.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.genres.some((g) => g.includes(q)) ||
          m.actors.some((a) => a.toLowerCase().includes(q))
      );
    }

    if (activeGenre !== '全部') {
      result = result.filter((m) => m.genres.includes(activeGenre));
    }

    if (activeRegion !== '全部') {
      const region = regions.find((r) => r.name === activeRegion);
      if (region) {
        result = result.filter((m) => m.region === activeRegion);
      }
    }

    if (activeYear !== '全部') {
      if (activeYear === '更早') {
        result = result.filter((m) => parseInt(m.year) < 2000);
      } else if (activeYear.includes('-')) {
        const [start, end] = activeYear.split('-').map(Number);
        result = result.filter((m) => {
          const y = parseInt(m.year);
          return y >= start && y <= end;
        });
      } else {
        result = result.filter((m) => m.year === activeYear);
      }
    }

    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      result.sort((a, b) => parseInt(b.year) - parseInt(a.year));
    }

    return result;
  }, [slug, searchQuery, activeGenre, activeRegion, activeYear, sortBy]);

  const filterSections = [
    {
      label: '类型',
      options: ['全部', ...genres],
      active: activeGenre,
      setActive: setActiveGenre,
    },
    {
      label: '地区',
      options: ['全部', ...regions.map((r) => r.name)],
      active: activeRegion,
      setActive: setActiveRegion,
    },
    {
      label: '年份',
      options: ['全部', ...years],
      active: activeYear,
      setActive: setActiveYear,
    },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-4" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">{categoryName}</span>
            </nav>

            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              {searchQuery ? `搜索"${searchQuery}"的结果` : `${categoryName}大全`}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {searchQuery
                ? `找到 ${filteredMovies.length} 个相关影视资源`
                : `天堂影院为您精选海量${categoryName}资源，高清流畅在线播放`}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-4 md:py-5 border-b border-background-200/70 sticky top-16 bg-background-50 z-30">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {filterSections.map((section) => (
              <div key={section.label} className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="text-xs text-foreground-400 font-medium w-10 flex-shrink-0">{section.label}：</span>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {section.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => section.setActive(option)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-all cursor-pointer whitespace-nowrap ${
                        section.active === option
                          ? 'bg-secondary-500 text-white'
                          : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
              <span className="text-xs text-foreground-400">
                共 <strong className="text-foreground-700">{filteredMovies.length}</strong> 部影视作品
              </span>
              <div className="flex items-center gap-1.5">
                {[
                  { key: 'latest', label: '最新' },
                  { key: 'rating', label: '评分' },
                  { key: 'year', label: '年份' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setSortBy(s.key)}
                    className={`px-3 py-1.5 rounded-full text-xs transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-primary-100 text-primary-700 font-medium'
                        : 'text-foreground-500 hover:text-foreground-800'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-6 md:py-8" aria-label="影视列表">
          <div className="max-w-7xl mx-auto">
            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredMovies.map((movie) => (
                  <Link
                    key={movie.id}
                    to={`/movie/${movie.id}`}
                    className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden transition-all hover:border-primary-200 cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden">
                      <img
                        src={movie.poster}
                        alt={movie.title}
                        title={`${movie.title} - ${categoryName} 高清在线播放 天堂影院`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md">
                        <i className="ri-star-fill text-accent-400 text-xs"></i>
                        <span>{movie.rating}</span>
                      </div>
                    </div>
                    <div className="p-3 md:p-4">
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {movie.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1 text-xs text-foreground-500">
                        <span>{movie.year}</span>
                        <span>·</span>
                        <span>{movie.region}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {movie.genres.slice(0, 2).map((genre) => (
                          <span key={genre} className="text-xs px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                            {genre}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 md:py-20">
                <div className="w-16 h-16 flex items-center justify-center text-foreground-300 mb-4">
                  <i className="ri-search-line text-4xl"></i>
                </div>
                <p className="text-foreground-500 text-sm">没有找到相关影视资源</p>
                <Link
                  to={`/category/${slug || 'movie'}`}
                  className="mt-4 px-4 py-2 text-sm bg-primary-500 text-white rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  清除筛选
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}